import type { ReactNode } from 'react';
import { GOTO_SHORTCUTS } from '@/app/navigation';
import { ui, useUi } from '@/app/ui-store';
import { Dialog, DialogBody, DialogContent, DialogHeader } from '@/components/ui/Dialog';
import { Kbd, ModKey } from '@/components/ui/Kbd';

function Row({ keys, label }: { keys: ReactNode; label: string }) {
  return (
    <div className="flex h-8 items-center justify-between gap-4 border-b border-border last:border-0">
      <span className="text-base text-fg-muted">{label}</span>
      <span className="flex items-center gap-1">{keys}</span>
    </div>
  );
}

export function ShortcutsDialog() {
  const { shortcutsOpen } = useUi();
  return (
    <Dialog open={shortcutsOpen} onOpenChange={ui.setShortcutsOpen}>
      <DialogContent size="lg">
        <DialogHeader
          title="Keyboard shortcuts"
          description="Move through Biller without leaving the keyboard."
        />
        <DialogBody className="grid grid-cols-1 gap-x-8 pb-5 sm:grid-cols-2">
          <div>
            <p className="mb-1 micro-label">General</p>
            <Row
              label="Command palette"
              keys={
                <>
                  <ModKey />
                  <Kbd>K</Kbd>
                </>
              }
            />
            <Row label="Search" keys={<Kbd>/</Kbd>} />
            <Row label="Toggle sidebar" keys={<Kbd>[</Kbd>} />
            <Row label="Show shortcuts" keys={<Kbd>?</Kbd>} />
            <p className="mt-4 mb-1 micro-label">Tables</p>
            <Row
              label="Next / previous row"
              keys={
                <>
                  <Kbd>↓</Kbd>
                  <Kbd>↑</Kbd>
                </>
              }
            />
            <Row label="Open row" keys={<Kbd>↵</Kbd>} />
            <Row
              label="Open in new tab"
              keys={
                <>
                  <ModKey />
                  <Kbd>click</Kbd>
                </>
              }
            />
          </div>
          <div>
            <p className="mb-1 micro-label max-sm:mt-4">Go to</p>
            {GOTO_SHORTCUTS.map((s) => (
              <Row
                key={s.key}
                label={s.label}
                keys={
                  <>
                    <Kbd>G</Kbd>
                    <Kbd>{s.key.toUpperCase()}</Kbd>
                  </>
                }
              />
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
